/**
 * Iter90 — Claim History Breadcrumb.
 *
 * Rendered on the coach/admin-facing player detail page. Shows every
 * assessment claim this player has raised across every coach, most-recent
 * first. Tiny by design — the point is to spot patterns (e.g. "claims 4.0
 * with three coaches") at a glance, not to be a detail editor. Each row
 * deep-links to the full claim detail page so a coach can drill in.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ClipboardList, ArrowRight, Check, Clock, X } from "lucide-react";

const STATUS_META = {
  pending:  { color: "#CCFF00", label: "Pending",  Icon: Clock },
  approved: { color: "#22C55E", label: "Approved", Icon: Check },
  rejected: { color: "#FF4500", label: "Rejected", Icon: X },
};

export default function ClaimHistoryBreadcrumb({ playerId }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    api.get(`/players/${playerId}/claim-history`)
      .then((r) => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [playerId]);

  if (items === null) {
    return <div className="label-mono text-[#525252]">Loading claim history…</div>;
  }
  if (items.length === 0) {
    // Deliberately hide the whole card when there's nothing to show — this
    // is a "signal-only" breadcrumb, not a persistent widget.
    return null;
  }

  return (
    <div className="card-surface p-5 fade-up" data-testid="claim-history-breadcrumb">
      <div className="flex items-start gap-3 mb-3">
        <ClipboardList className="w-5 h-5 text-[#A3A3A3] shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <div className="label-mono text-[#A3A3A3]">CLAIM HISTORY · {items.length}</div>
          <div className="text-xs text-[#525252] leading-relaxed mt-0.5">
            Every prior-assessment claim this player has raised. Useful for spotting patterns across coaches.
          </div>
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map((c) => {
          const meta = STATUS_META[c.status] || STATUS_META.pending;
          const StatusIcon = meta.Icon;
          return (
            <Link
              key={c.id}
              to={`/assessment-claims/${c.id}`}
              data-testid={`claim-history-row-${c.id}`}
              className="border border-[#262626] p-3 flex items-center gap-3 hover:bg-[#141414] active:scale-[0.99] transition-all"
            >
              <div
                className="w-8 h-8 flex items-center justify-center shrink-0"
                style={{ background: meta.color, color: "#0A0A0A" }}
                title={meta.label}
              >
                <StatusIcon className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white truncate">
                  {c.sport ? c.sport[0].toUpperCase() + c.sport.slice(1) : "Sport"}
                  {" · claimed "}
                  <span className="text-[#CCFF00]">{c.prior_level ?? "?"}</span>
                  {typeof c.awarded_level === "number" && (
                    <>
                      {" → awarded "}
                      <span style={{ color: meta.color }}>{c.awarded_level}</span>
                    </>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5 truncate">
                  {c.coach_name || "Coach"} · {new Date(c.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  {c.decided_at && (
                    <> · decided {new Date(c.decided_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</>
                  )}
                </div>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-[#525252] shrink-0" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
